/**
 * 16 - Relay Mast (10 §2): the radio mast and its generator hut share a small wired compound on the hilltop.
 * The mast opens to a time bomb only; the generator is soft, stands among its fuel drums, and feeds every
 * searchlight on the map - kill it first and the lamps go dark (one blackout group, wired last of all so the
 * lamps the budget buys are on it too).
 */

import { siteEmplacement } from '../emplacements';
import { PROPS } from '../../sim/props';
import { checker, guard, guardAtDoor, guardsAbout, insideRect, required, structure } from './helpers';
import { dress, lamp, put } from './helpers3a';
import { checkBlackouts, checkTargets } from './helpers3aAsserts';
import type { ArchetypeSpec } from './types';

/** The compound's own lamps: there is always something for the generator to feed. */
export const lampsFor = (threat: number): number => (threat >= 5 ? 3 : 2);

export const relayMast: ArchetypeSpec = {
  id: 'relay-mast',
  name: 'RELAY MAST',
  nativeBiome: 'arctic',
  baseThreat: 3,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [40, 46], d: [30, 32] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const cx = (g.rect.x0 + g.rect.x1) / 2;
    const cz = (g.rect.z0 + g.rect.z1) / 2;
    const mast = put(g, 'relayMast', cx, cz - 3, { target: true, margin: 2 });
    // The generator hut stands off to one side, the cable run between them; its drums are stacked against its wall.
    const at = [{ x: mast.x, z: mast.z }];
    const beside = { target: true, near: mast, maxDist: 15, minDist: 9, from: at, margin: 1.2 };
    const generator = required(g, 'generatorHut', beside);
    // Two metres off its north wall: near enough that a drum going up takes the generator with it.
    const b = g.map.bounds('generatorHut', generator.x, generator.z);
    for (const dx of [-1.5, 1]) dress(g, 'barrel', generator.x + dx, b.z0 - 2, { margin: 0.1 });

    const lamps = lampsFor(g.threat);
    // The south corners first, then wherever else along the wire there is room.
    const spots = [
      [g.inner.x0 + 2, g.inner.z1 - 2], [g.inner.x1 - 2, g.inner.z1 - 2], [cx, g.inner.z0 + 2],
      [g.inner.x0 + 2, g.inner.z0 + 2], [g.inner.x1 - 2, g.inner.z0 + 2],
      [cx - 8, g.inner.z1 - 2], [cx + 8, g.inner.z1 - 2],
    ] as const;
    let lit = 0;
    for (const [x, z] of spots) if (lit < lamps && lamp(g, x, z)) lit++;
    if (lit < lamps) g.map.problems.push('relay-mast: no room for the compound searchlights');
    if (g.threat >= 5) siteEmplacement(g.ctx, g.rng, 'mgNest', { x: g.ctx.plan.roadEnd.x, z: g.rect.z1 }, g.elite);

    const hut = structure(g, 'hut', { minDist: 8, from: at });
    structure(g, 'alarmPost', { margin: 1 });
    if (hut) guardAtDoor(g, hut);
    guardAtDoor(g, generator);
    guard(g, mast.x + 2.5, mast.z + 3, 0);
    guardsAbout(g, g.rng.int(2));
    return {
      objective: {
        kind: 'destroyTargets', label: 'BRING DOWN THE MAST', targetPropIds: [mast.id, generator.id], minFraction: 1,
        rules: [{ type: 'recall', mode: 'normal' }],
      },
      mission: { blackoutGroups: [{ switchPropId: generator.id, searchlightPropIds: [], spawnIndexes: [] }] },
      // Last of all: every lamp on the finished map hangs on the generator, whoever bought it.
      finish(def) {
        const group = def.blackoutGroups?.[0];
        if (group) group.searchlightPropIds = def.props.filter((p) => p.kind === 'searchlight').map((p) => p.id);
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('relay-mast');
    const targets = checkTargets(check, def, ['relayMast', 'generatorHut']);
    const masts = targets.filter((t) => t.kind === 'relayMast');
    const generators = targets.filter((t) => t.kind === 'generatorHut');
    check(masts.length === 1 && generators.length === 1, 'needs exactly one mast and one generator hut');
    check(def.objective.kind === 'destroyTargets' && def.objective.minFraction === 1, 'both must come down');
    check(
      PROPS.relayMast.durability === 'bombOnly' && PROPS.generatorHut.durability === 'soft',
      'mast: bomb only; hut: soft',
    );
    check(targets.every((t) => insideRect(def.goalZone, t.x, t.z)), 'mast and generator share the hilltop compound');
    checkBlackouts(check, def, generators);
    const lamps = def.props.filter((p) => p.kind === 'searchlight');
    const own = lamps.filter((p) => insideRect(def.goalZone, p.x, p.z)).length;
    check(
      own >= lampsFor(def.threat),
      `the compound needs ${lampsFor(def.threat)} searchlights of its own, has ${own}`,
    );
    check(
      def.blackoutGroups?.[0]?.searchlightPropIds.length === lamps.length,
      'every searchlight on the map hangs on the generator',
    );
    return out;
  },
};
